import { API } from "../../../../request/api";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AddressTypeEnum,
  useAccountAddressCreateMutation,
  useAccountAddressDeleteMutation,
  useAccountAddressUpdateMutation,
  useAddressValidationRulesQuery,
  useMyAddressesQuery,
} from "../../../../saleor/api";
import { Note } from "../../../../helbest";
import { showAllErrors } from "../../../../base/utils";
import { AddressItemTransformer } from "../../../../services/objectTransformer/address";
import { useTranslation } from "react-i18next";
import {
  ADDRESS_ACTION_TYPE,
  CLEAR,
  CREATE,
  EDIT,
  UPDATE_ADDRESS,
} from "../../../../store/actions/address_actions";
import { countries } from "../../../../constants/countries-code";

const useAddress = () => {
  const { Login, Address: state } = useSelector((state) => state);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [accountAddressCreateMutation] = useAccountAddressCreateMutation();
  const [accountAddressUpdateMutation] = useAccountAddressUpdateMutation();
  const [accountAddressDeleteMutation] = useAccountAddressDeleteMutation();

  const { data, loading, refetch } = useMyAddressesQuery({
    onCompleted: (response) => {
      if (!response.me.addresses.length) {
        dispatch(CREATE());
      }
    },
    onError: (error) => {
      Note.error(error?.message);
    },
    skip: !Login.token,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!Login.token) return;
    refetch?.();
  }, [Login.token, refetch]);

  const addresses =
    data?.me?.addresses.map((address) => AddressItemTransformer(address)) || [];

  // eslint-disable-next-line
  const { data: regionsData } = useAddressValidationRulesQuery({
    variables: {
      countryCode:
        state.address.country ||
        countries.find((country) => country.code === Login.country_code)
          ?.country_code,
      city: state.address.city,
    },
    skip: !state.mapModalOpen && !state.address.country,
    onError: (error) => {
      Note.error(error?.message);
    },
  });

  const addressValidationRules = regionsData?.addressValidationRules;

  const onDelete = (id) => {
    return accountAddressDeleteMutation({
      variables: {
        id,
      },
      onCompleted: (response) => {
        if (!response.accountAddressDelete.errors.length) {
          refetch();
          Note.success(t("address_map.deleted"));
        } else {
          showAllErrors(response.accountAddressDelete.errors);
        }
      },
    });
  };

  const onSubmit = ({ streetAddress1, streetAddress2, city, postalCode }) => {
    const newAddress = {};
    const address = {
      ...state.address,
      streetAddress1,
      streetAddress2,
      city,
      postalCode,
    };
    addressValidationRules.allowedFields.forEach((field) => {
      newAddress[field] = address[field];
    });
    newAddress.metadata = [
      {
        key: "location",
        value: `${address.location.lng}, ${address.location.lat}`,
      },
    ];
    newAddress.streetAddress2 = address.streetAddress2;
    newAddress.country = address.country;
    newAddress.countryArea = address.streetAddress2;

    if (state.status === ADDRESS_ACTION_TYPE.EDIT) {
      return accountAddressUpdateMutation({
        variables: {
          id: address.id,
          input: newAddress,
          channel: Login.channel,
        },
        onCompleted: (response) => {
          if (!response.accountAddressUpdate.errors.length) {
            refetch();
            Note.success(t("address_map.updated"));
            dispatch(CLEAR());
          } else {
            showAllErrors(response.accountAddressUpdate.errors);
          }
        },
      });
    } else {
      return accountAddressCreateMutation({
        variables: {
          type: AddressTypeEnum.Shipping,
          input: newAddress,
          channel: Login.channel,
        },
        onCompleted: (response) => {
          if (!response.accountAddressCreate.errors.length) {
            refetch();
            Note.success(t("address_map.created"));
            dispatch(CLEAR());
          } else {
            showAllErrors(response.accountAddressCreate.errors);
          }
        },
        onError: (error) => {
          if (error) {
          }
        },
      });
    }
  };

  const onSelectLocation = async () => {
    return API.get
      .addressByUserLocation({
        lng: state.address?.location.lng,
        lat: state.address?.location.lat,
      })
      .then((response) => {
        console.log("response :: ", response?.code);
        dispatch(
          UPDATE_ADDRESS({
            country: response.address.country_code.toUpperCase(),
            countryArea: response.address.state,
            city:
              "city" in response.address
                ? response.address.city
                : "village" in response.address
                  ? response.address.village
                  : "town" in response.address
                    ? response.address.town
                    : "",
            streetAddress1: response.address.road,
            streetAddress2: response.address.state,
          }),
        );
      });
  };

  const onEditModalOpen = (id) => {
    const address = addresses.find((address) => address.id === id);
    dispatch(
      EDIT({
        id: address.id,
        streetAddress1: address.address,
        streetAddress2: address.details,
        countryArea: address.details,
        country: address.country,
        city: address.city,
        postalCode: address.postal_code,
        location: address.location,
      }),
    );
  };

  const onCreateModal = () => {
    dispatch({
      type: ADDRESS_ACTION_TYPE.CREATE,
    });
  };

  return {
    addresses,
    onSelectLocation,
    onEditModalOpen,
    loading,
    onDelete,
    onCreateModal,
    onSubmit,
  };
};

export default useAddress;
